import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MediaFile, useMediaStore } from '../store/media';
import { useCreatorStore, SavedDesign } from '../store/creator';
import { MediaUpload } from './MediaUpload';
import { MediaPicker } from './MediaPicker';
import { PostPreview } from './PostPreview';
import { PostType, FORMAT_SPECS, Platform } from '../config/formatSpecs';

// Post-Type-Auswahl mit Meta-Namen und Plattform-Kompatibilität
const POST_TYPE_OPTIONS: {
  id: PostType;
  icon: string;
  platforms: Platform[];
}[] = [
  { id: 'feed', icon: '📄', platforms: ['facebook', 'instagram'] },
  { id: 'reel', icon: '🎬', platforms: ['facebook', 'instagram'] },
  { id: 'story', icon: '📲', platforms: ['facebook', 'instagram'] },
  { id: 'carousel', icon: '🎠', platforms: ['facebook', 'instagram'] },
];

// Funktion um Meta-Format-Namen für Plattform und PostType zu bekommen
function getMetaFormatName(platform: Platform, postType: PostType, hasVideo: boolean): string {
  const spec = FORMAT_SPECS.find(
    (s) =>
      s.platform === platform &&
      s.postType === postType &&
      (postType === 'reel' || postType === 'story' || s.mediaType === (hasVideo ? 'video' : 'image') || s.mediaType === 'both')
  );
  return spec?.nameDe || `${platform} ${postType}`;
}

type MediaSource = 'upload' | 'library' | 'designs';

export interface CreatePostData {
  project_id: string;
  text: string;
  platforms: string[];
  post_type?: PostType;
  scheduled_for: string;
  status: 'draft' | 'scheduled';
  media_urls?: string[];
  flight_id?: string | null;
}

export interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onCreate: (data: CreatePostData) => Promise<void>;
  defaultDate?: Date | null;
  flights: { id: string; name: string; display_id: string; status: string }[];
}

export function CreatePostModal({
  isOpen,
  onClose,
  projectId,
  onCreate,
  defaultDate,
  flights,
}: CreatePostModalProps) {
  const { t } = useTranslation(['posts', 'common']);
  const { media: libraryMedia } = useMediaStore();
  const { savedDesigns, fetchSavedDesigns } = useCreatorStore();
  const [text, setText] = useState('');
  const [platforms, setPlatforms] = useState<string[]>(['facebook']);
  const [scheduledFor, setScheduledFor] = useState('');
  const [status, setStatus] = useState<'draft' | 'scheduled'>('scheduled');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedPostType, setSelectedPostType] = useState<PostType>('feed');
  const [libraryMediaIds, setLibraryMediaIds] = useState<string[]>([]);
  const [libraryMediaUrls, setLibraryMediaUrls] = useState<string[]>([]);
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
  const [mediaSource, setMediaSource] = useState<MediaSource>('upload');
  const [selectedDesigns, setSelectedDesigns] = useState<SavedDesign[]>([]);

  // Load designs when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchSavedDesigns(projectId);
    }
  }, [isOpen, projectId, fetchSavedDesigns]);

  // Kombiniere hochgeladene, Bibliotheks-Medien und Designs für die Vorschau
  const allMediaForPreview = useMemo(() => {
    const librarySelected = libraryMedia.filter((m) => libraryMediaIds.includes(m.id));
    // Convert designs to MediaFile-like objects for preview
    const designsAsMedia: MediaFile[] = selectedDesigns.map(d => ({
      id: d.id,
      project_id: d.project_id,
      uploaded_by: '',
      post_id: null,
      post_ids: [],
      flight_id: d.flight_id || null,
      filename: d.name,
      original_name: d.name,
      mime_type: 'image/png',
      size: 0,
      url: d.thumbnail_url || '',
      delete_after: null,
      format_category: d.category === 'story' || d.category === 'reel' ? 'story' : d.category === 'post' ? 'square' : null,
      source: 'design' as const,
      stock_source: null,
      stock_id: null,
      attribution: null,
      created_at: d.created_at,
      updated_at: d.updated_at,
    }));
    return [...selectedMedia, ...librarySelected, ...designsAsMedia];
  }, [selectedMedia, libraryMedia, libraryMediaIds, selectedDesigns]);

  useEffect(() => {
    if (defaultDate) {
      // Use the selected date from calendar
      // FullCalendar returns UTC midnight for day clicks, convert to local time
      const localDate = new Date(defaultDate);
      // Set a reasonable time (10:00) for the selected date
      localDate.setHours(10, 0, 0, 0);
      // Format as local datetime for the input
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const hours = String(localDate.getHours()).padStart(2, '0');
      const minutes = String(localDate.getMinutes()).padStart(2, '0');
      setScheduledFor(`${year}-${month}-${day}T${hours}:${minutes}`);
    } else {
      // Set default to tomorrow at 10:00
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
      const day = String(tomorrow.getDate()).padStart(2, '0');
      const hours = String(tomorrow.getHours()).padStart(2, '0');
      const minutes = String(tomorrow.getMinutes()).padStart(2, '0');
      setScheduledFor(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  }, [isOpen, defaultDate]);

  useEffect(() => {
    // Reset media when modal closes
    if (!isOpen) {
      setSelectedMedia([]);
      setShowPreview(false);
      setLibraryMediaIds([]);
      setLibraryMediaUrls([]);
      setSelectedPostType('feed');
      setSelectedFlightId(null);
      setMediaSource('upload');
      setSelectedDesigns([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePlatformToggle = (platform: string) => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (platforms.length === 0) return;

    // Combine uploaded media, library media, and design thumbnails, filter out empty strings
    const allMediaUrls = [
      ...selectedMedia.map((m) => m.url),
      ...libraryMediaUrls,
      ...selectedDesigns.map((d) => d.thumbnail_url).filter(Boolean),
    ].filter((url) => url && url.trim() !== '') as string[];

    // Instagram requires at least one image
    if (platforms.includes('instagram') && allMediaUrls.length === 0) {
      alert(t('posts:instagram.requiresMedia'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreate({
        project_id: projectId,
        text,
        platforms,
        post_type: selectedPostType,
        scheduled_for: new Date(scheduledFor).toISOString(),
        status,
        media_urls: allMediaUrls,
        flight_id: selectedFlightId,
      });
      setText('');
      setPlatforms(['facebook']);
      setStatus('scheduled');
      setSelectedMedia([]);
      setLibraryMediaIds([]);
      setLibraryMediaUrls([]);
      setSelectedPostType('feed');
      setSelectedFlightId(null);
      setMediaSource('upload');
      setSelectedDesigns([]);
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">{t('posts:create.title')}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className={`px-3 py-1.5 text-sm rounded-md ${
                showPreview ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {showPreview ? t('posts:detail.editor') : t('posts:detail.preview')}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {showPreview ? (
            <PostPreview
              text={text}
              platforms={platforms}
              media={allMediaForPreview}
              scheduledFor={scheduledFor}
              selectedPostType={selectedPostType}
            />
          ) : (
            <form onSubmit={handleSubmit} id="create-post-form">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('posts:text')}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('posts:create.placeholder')}
              required
            />
            <p className="text-sm text-gray-500 mt-1">{t('posts:detail.characters', { count: text.length })}</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('posts:platforms')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={platforms.includes('facebook')}
                  onChange={() => handlePlatformToggle('facebook')}
                  className="mr-2 h-4 w-4 text-blue-600 rounded"
                />
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Facebook
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={platforms.includes('instagram')}
                  onChange={() => handlePlatformToggle('instagram')}
                  className="mr-2 h-4 w-4 text-pink-600 rounded"
                />
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-1 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  Instagram
                </span>
              </label>
            </div>
            {platforms.length === 0 && (
              <p className="text-sm text-red-500 mt-1">{t('posts:create.selectPlatform')}</p>
            )}
          </div>

          {/* Geplant für & Flight - nebeneinander */}
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('posts:scheduledFor')}
              </label>
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Flight Selection */}
            {flights.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('posts:flight')}
                </label>
                <select
                  value={selectedFlightId || ''}
                  onChange={(e) => setSelectedFlightId(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">{t('posts:noFlight')}</option>
                  {flights.map((flight) => (
                    <option key={flight.id} value={flight.id}>
                      {flight.name} ({flight.display_id})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Post-Type Selection with Meta Format Names */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('posts:create.postTypeLabel')}
            </label>
            <div className="flex flex-wrap gap-2">
              {POST_TYPE_OPTIONS.map((option) => {
                // Prüfen ob alle gewählten Plattformen diesen Post-Type unterstützen
                const isCompatible = platforms.every((p) =>
                  option.platforms.includes(p as Platform)
                );
                const isSelected = selectedPostType === option.id;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => isCompatible && setSelectedPostType(option.id)}
                    disabled={!isCompatible}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : isCompatible
                        ? 'border-gray-200 hover:border-gray-300 text-gray-700'
                        : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <span className="text-lg">{option.icon}</span>
                    <div className="text-left">
                      <div className="text-sm font-medium">{t(`posts:postType.${option.id}`)}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Meta Format-Namen für gewählte Plattformen anzeigen */}
            {platforms.length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">{t('posts:detail.metaApiFormats')}:</p>
                <div className="flex flex-wrap gap-2">
                  {platforms.map((platform) => {
                    const hasVideo = allMediaForPreview.some((m) => m.mime_type.startsWith('video/'));
                    const formatName = getMetaFormatName(platform as Platform, selectedPostType, hasVideo);
                    return (
                      <span
                        key={platform}
                        className={`px-2 py-1 text-xs rounded-full ${
                          platform === 'facebook'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-pink-100 text-pink-800'
                        }`}
                      >
                        {formatName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Media Section with Tabs */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('posts:create.mediaAndDesigns')}
            </label>

            {/* Media Source Tabs */}
            <div className="flex border-b border-gray-200 mb-3">
              <button
                type="button"
                onClick={() => setMediaSource('upload')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  mediaSource === 'upload'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('posts:create.uploadTab')}
                {selectedMedia.length > 0 && (
                  <span className="ml-1 bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full text-xs">
                    {selectedMedia.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setMediaSource('library')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  mediaSource === 'library'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('posts:create.libraryTab')}
                {libraryMediaIds.length > 0 && (
                  <span className="ml-1 bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full text-xs">
                    {libraryMediaIds.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setMediaSource('designs')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  mediaSource === 'designs'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('posts:create.designsTab')}
                {selectedDesigns.length > 0 && (
                  <span className="ml-1 bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full text-xs">
                    {selectedDesigns.length}
                  </span>
                )}
              </button>
            </div>

            {/* Tab Content */}
            {mediaSource === 'upload' && (
              <MediaUpload
                projectId={projectId}
                selectedMedia={selectedMedia}
                onMediaSelect={setSelectedMedia}
                maxFiles={10}
              />
            )}

            {mediaSource === 'library' && (
              <MediaPicker
                projectId={projectId}
                selectedMediaIds={libraryMediaIds}
                onSelectionChange={(ids, urls) => {
                  setLibraryMediaIds(ids);
                  setLibraryMediaUrls(urls);
                }}
                maxSelection={10 - selectedMedia.length - selectedDesigns.length}
              />
            )}

            {mediaSource === 'designs' && (
              <div className="border border-gray-200 rounded-lg p-3">
                {savedDesigns.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    <p>{t('posts:create.noDesigns')}</p>
                    <p className="text-sm mt-1">
                      <Link to="/creator" className="text-blue-600 hover:underline">
                        {t('posts:create.toCreatorStudio')}
                      </Link>
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                    {savedDesigns.map((design) => {
                      const isSelected = selectedDesigns.some(d => d.id === design.id);
                      return (
                        <button
                          key={design.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedDesigns(prev => prev.filter(d => d.id !== design.id));
                            } else {
                              setSelectedDesigns(prev => [...prev, design]);
                            }
                          }}
                          className={`relative group rounded-lg overflow-hidden aspect-square border-2 transition-all ${
                            isSelected
                              ? 'border-purple-500 ring-2 ring-purple-200'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {design.thumbnail_url ? (
                            <img
                              src={design.thumbnail_url}
                              alt={design.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {/* Selection indicator */}
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-purple-500 text-white rounded-full p-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                          {/* Name overlay on hover */}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-xs truncate">{design.name}</p>
                            <p className="text-white/70 text-xs">{design.display_id}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Selected media summary */}
            {allMediaForPreview.length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  {t('posts:create.mediaSelected', { count: allMediaForPreview.length })}
                  {selectedMedia.length > 0 && ` (${t('posts:create.uploadedCount', { count: selectedMedia.length })})`}
                  {libraryMediaIds.length > 0 && ` (${t('posts:create.libraryCount', { count: libraryMediaIds.length })})`}
                  {selectedDesigns.length > 0 && ` (${t('posts:create.designCount', { count: selectedDesigns.length })})`}
                </p>
              </div>
            )}

            {platforms.includes('instagram') && allMediaForPreview.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">
                {t('posts:instagram.requiresMedia')}
              </p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common:status')}
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="scheduled"
                  checked={status === 'scheduled'}
                  onChange={() => setStatus('scheduled')}
                  className="mr-2"
                />
                {t('posts:create.scheduleNow')}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={status === 'draft'}
                  onChange={() => setStatus('draft')}
                  className="mr-2"
                />
                {t('posts:saveDraft')}
              </label>
            </div>
          </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('common:cancel')}
          </button>
          <button
            type="submit"
            form="create-post-form"
            disabled={isSubmitting || (!text && selectedDesigns.length === 0 && selectedMedia.length === 0 && libraryMediaIds.length === 0) || platforms.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? t('posts:create.creating') : status === 'draft' ? t('common:save') : t('posts:create.schedule')}
          </button>
        </div>
      </div>
    </div>
  );
}
